import React, { useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
// import { Search } from "lucide-react";
import { ListFilter, Lock, Upload, X } from "lucide-react";
// import InputCst from "@components/InputCst/InputCst";
import FiltersAccordionCst from "@components/FiltersAccordionCst";
import { FiltersBarValue } from "@components/FiltersBarCst/types";
import ListItemCardCst from "@components/ListItemCardCst";
import PaginatorCst from "@components/PaginatorCst";
import UploadModalCst from "@components/UploadModalCst";
import { ButtonSecondary } from "src/UI/ButtonSecondary";
import { usePaginatedList } from "@hooks/usePaginatedList";
import { ServicesURL, downloadFile } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import { WizardStepProps } from "@components/WizardCst/types";
import { StaticGroupWizardData } from "../types";
import { useStaticGroupWizardContext } from "../staticGroupWizardContext";
import { StepContainer } from "../../Wizard/components/StepLayout";

const PAGE_SIZE = 10;

const AddUsersStep: React.FC<WizardStepProps<StaticGroupWizardData>> = ({
  data,
  setData,
}) => {
  const { t } = useTranslation("group-wizard-v2");
  const { t: tInput } = useTranslation("input");
  const theme = useTheme();
  const { id: corporateId = "" } = useParams();
  const { groupId } = useStaticGroupWizardContext();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  // const [search, setSearch] = useState("");
  const [fieldFilters, setFieldFilters] = useState<FiltersBarValue>({});
  const [filtersPanelOpen, setFiltersPanelOpen] = useState(false);
  const sort = "most-recent";
  const [csvOpen, setCsvOpen] = useState(false);

  const handleCsvUpload = (file: File) => {
    setData({ pendingCsvFile: file });
    return Promise.resolve({
      successMessage: String(
        t(
          groupId
            ? "csv-modal.deferred-success-edit"
            : "csv-modal.deferred-success",
        ),
      ),
    });
  };

  const listUrl = corporateId
    ? `${ServicesURL.getCustomer}?corporate_id=${corporateId}`
    : null;

  const hasActiveFilters = useMemo(
    () =>
      Object.values(fieldFilters || {}).some((val) => {
        if (val === undefined || val === null || val === "") return false;
        if (Array.isArray(val) && val.length === 0) return false;
        return true;
      }),
    [fieldFilters],
  );

  const { items, totalItems, totalPages, isLoading } = usePaginatedList(
    listUrl,
    // { page, pageSize, search: search || undefined, sort },
    { page, pageSize, fieldFilters, sort },
  );

  const selected = new Set(data.userIds);

  const toggle = (userId: number, next: boolean) => {
    setData((prev) => {
      const set = new Set(prev.userIds);
      if (next) set.add(userId);
      else set.delete(userId);
      return { userIds: Array.from(set) };
    });
  };

  return (
    <StepContainer>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          {/* <Box
          sx={{
            flex: 1,
            minWidth: 240,
            "& .MuiFormControl-root": {
              minWidth: "0 !important",
              width: "100% !important",
            },
            "& .MuiOutlinedInput-root": { borderRadius: "8px" },
            "& .MuiFormHelperText-root": { display: "none" },
          }}
        >
          <InputCst
            id="group-users-search"
            placeholder={t("search")}
            inputSearch
            value={search}
            onChange={(e: any) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            startAdornment={
              <Search
                size={16}
                color={theme.palette.systemSecondary01}
                style={{ marginRight: 8 }}
              />
            }
          />
        </Box> */}

          <ButtonSecondary
            size="md"
            endIcon={<ListFilter size={16} />}
            onClick={() => setFiltersPanelOpen((prev) => !prev)}
            sxButton={
              hasActiveFilters || filtersPanelOpen
                ? {
                    flexShrink: 0,
                    border: `2px solid ${theme.palette.systemSecondary01} !important`,
                  }
                : { flexShrink: 0 }
            }
          >
            {t("filtersButton")}
          </ButtonSecondary>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <ButtonSecondary
              size="md"
              endIcon={<Upload size={16} />}
              onClick={() => setCsvOpen(true)}
              sxButton={{ flexShrink: 0 }}
            >
              {t("uploadCsv")}
            </ButtonSecondary>

            {data.pendingCsvFile && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  padding: "4px 8px",
                  borderRadius: "8px",
                  border: `1px solid ${theme.palette.divider}`,
                  maxWidth: 220,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: theme.palette.text.primary,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {data.pendingCsvFile.name}
                </Typography>
                <IconButton
                  size="small"
                  aria-label={String(t("csv-modal.remove-pending"))}
                  onClick={() => setData({ pendingCsvFile: null })}
                  sx={{ color: theme.palette.text.primary, p: "2px" }}
                >
                  <X size={14} />
                </IconButton>
              </Box>
            )}
          </Box>
        </Box>

        <FiltersAccordionCst
          open={filtersPanelOpen}
          onClose={() => setFiltersPanelOpen(false)}
          pageId={PagesID["corporate.customers"]}
          value={fieldFilters}
          onApply={(next) => {
            setFieldFilters(next);
            setPage(1);
          }}
        />
      </Box>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : items.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 4,
            color: theme.palette.text.tertiary,
          }}
        >
          {t("emptyUsers")}
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {items.map((user: any) => {
            const fullName = [user.name, user.surname]
              .filter(Boolean)
              .join(" ")
              .trim();
            const status = String(user.status || "").toLowerCase();
            return (
              <ListItemCardCst
                key={user.id_user}
                title={fullName || user.email || String(user.id_user)}
                subtitle={{ id: user.id_user, activityType: user.email }}
                badges={
                  status === "active"
                    ? [{ label: t("active"), variant: "success" }]
                    : status === "suspended"
                      ? [
                          {
                            label: String(
                              tInput("options.status_user.suspended"),
                            ),
                            variant: "error",
                            icon: <Lock />,
                          },
                        ]
                      : []
                }
                actions={[]}
                selectable
                selected={selected.has(user.id_user)}
                onSelect={(next) => toggle(user.id_user, next)}
              />
            );
          })}
        </Box>
      )}

      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <PaginatorCst
            page={page}
            pageSize={pageSize}
            totalItems={totalItems}
            onPageChange={setPage}
            onPageSizeChange={(next) => {
              setPageSize(next);
              setPage(1);
            }}
          />
        </Box>
      )}

      <UploadModalCst
        open={csvOpen}
        onClose={() => setCsvOpen(false)}
        title={String(t("uploadCsv"))}
        accept=".csv,text/csv"
        banner={{
          title: String(t("csv-modal.banner-title")),
          text: String(t("csv-modal.banner-text")),
          downloadLabel: String(t("csv-modal.banner-download")),
          actionsAlign: "center",
          onDownload: () =>
            downloadFile(
              `${ServicesURL.staticGroups}/static/group/download/template`,
            ),
          ...(groupId
            ? {
                secondaryDownloadLabel: String(
                  t("csv-modal.banner-download-members"),
                ),
                onSecondaryDownload: () =>
                  downloadFile(
                    `${ServicesURL.staticGroups}/static/${groupId}/users/external-id`,
                  ),
              }
            : {}),
        }}
        onUpload={handleCsvUpload}
      />
    </StepContainer>
  );
};

export default AddUsersStep;
